import api from '../../../api';
import { constants } from '../../../shared/enums/constants';
import { ResponseSuccess } from '../../../shared/types';
import {
  SpamAssassinScoreReportFilter,
  TestTemplateAction,
  TestType,
} from '../enums/inbox-radar';
import {
  GetEmailAuthenticationPayload,
  GetShareableReportDataPayload,
  GetShareableReportSenderEmailAccountsPayload,
  GetTestListRequestPayload,
  SeedTestPayload,
  TestGraphPayload,
  TestReportExportPayload,
  TestReportGraphPayload,
  TestReportListRequestPayload,
  TestReportSummaryPayload,
  UpdateTestStatusRequestPayload,
  CreateUpdateTest,
  DraftSeedTestPayload,
} from '../types';

export const getTestGraphData = (payload: TestGraphPayload) =>
  api.post<ResponseSuccess>(
    'inbox-analyzer/seed-test-template/analytics',
    payload,
  );

export const getTestSummaryData = (payload: TestGraphPayload) =>
  api.post<ResponseSuccess>(
    'inbox-analyzer/seed-test-template/summary',
    payload,
  );

export const getTestList = (payload: GetTestListRequestPayload) =>
  api.post<ResponseSuccess>('inbox-analyzer/seed-test-template/list', payload);

export const updateTestStatus = (
  testTemplateId: string,
  action: TestTemplateAction,
  payload?: UpdateTestStatusRequestPayload,
) =>
  api.patch<ResponseSuccess>(
    `inbox-analyzer/seed-test-template/${testTemplateId}/${action}`,
    payload,
  );

export const reRunTest = (testTemplateId: string) =>
  api.get<ResponseSuccess>(
    `inbox-analyzer/seed-test-template/${testTemplateId}/re-run`,
  );

export const seedTest = (
  params?: {
    templateType?: TestType;
  },
  payload?: SeedTestPayload,
) =>
  api.post<ResponseSuccess>('/inbox-analyzer/seed-test-template', payload, {
    params,
  });

export const getSeedTest = (id: string) =>
  api.get<ResponseSuccess>(`/inbox-analyzer/seed-test-template/${id}/config`);

export const getSeedTestTemplateReviewSendingLimit = (
  id: string,
  page: number,
) =>
  api.get<ResponseSuccess>(
    `/inbox-analyzer/seed-test-template/${id}/review-sending-limit`,
    {
      params: {
        page,
        limit: 15,
      },
    },
  );

export const getSeedTestTemplateWarningBannerConfig = (id: string) =>
  api.get<ResponseSuccess>(
    `/inbox-analyzer/seed-test-template/${id}/warning-banner-config`,
  );

export const updateSeedTest = (id: string, payload: CreateUpdateTest) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test-template/${id}/launch`,
    payload,
  );

export const getAllNotLinkedEmailAccountsInInboxAnalyzer = async (
  testTemplateId: string,
  clientIds: number[],
  search: string,
  pageNum: number,
  tagIds: number[],
) =>
  api.post(
    `/inbox-analyzer/seed-test-template/${testTemplateId}/add-emails/list`,
    {
      pageSize: constants.DEFAULT_PAGE_SIZE,
      pageNum,
      search,
      ...(clientIds && { clientIds }),
      ...(tagIds && { tagIds }),
    },
  );

export const getNotLinkedEmailAccountsCountInInboxAnalyzer = async (
  testTemplateId: string,
  search: string,
  clientIds: number[],
  tagIds: number[],
) =>
  api.post(
    `/inbox-analyzer/seed-test-template/${testTemplateId}/add-emails/list-total-count`,
    {
      search,
      ...(clientIds && { clientIds }),
      ...(tagIds && { tagIds }),
    },
  );

export const getLinkedEmailAccountsInInboxAnalyzer = async (
  testTemplateId: string,
  pageNum: number,
) =>
  api.get(
    `/inbox-analyzer/seed-test-template/${testTemplateId}/connected-email-accounts`,
    {
      params: {
        pageSize: 10,
        pageNum,
      },
    },
  );

export const addRemoveLinkedEmailAccountsInInboxAnalyzer = async (
  testTemplateId: string,
  emailAccountIdsToAdd: number[],
  emailAccountIdsToRemove: number[],
) =>
  api.put(
    `/inbox-analyzer/seed-test-template/${testTemplateId}/email-accounts`,
    {
      emailAccountIdsToAdd,
      emailAccountIdsToRemove,
    },
  );

export const draftSeedTest = (
  seedTestTemplateId: string,
  payload: DraftSeedTestPayload,
) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test-template/${seedTestTemplateId}`,
    payload,
  );

export const getTestConfig = (seedTestId: string) =>
  api.get<ResponseSuccess>(`/inbox-analyzer/seed-test/${seedTestId}/config`);

export const getTestReportGraphData = (
  seedTemplateId: string,
  payload: TestReportGraphPayload,
) =>
  api.post<ResponseSuccess>(
    `inbox-analyzer/seed-test-template/${seedTemplateId}/analytics`,
    payload,
  );

export const getTestReportSummaryData = (
  seedTestId: string,
  payload: TestReportSummaryPayload,
) =>
  api.post<ResponseSuccess>(
    `inbox-analyzer/seed-test/${seedTestId}/summary`,
    payload,
  );

export const getTestReportList = (
  seedTestId: string,
  payload: TestReportListRequestPayload,
) =>
  api.post<ResponseSuccess>(
    `inbox-analyzer/seed-test/${seedTestId}/list`,
    payload,
  );

export const getTestReportEmails = (seedTestId: string) =>
  api.get<ResponseSuccess>(
    `/inbox-analyzer/seed-test/${seedTestId}/email-accounts`,
  );

export const getTestPreviewEmail = (seedTestId: string) =>
  api.get<ResponseSuccess>(
    `/inbox-analyzer/seed-test/${seedTestId}/view-email`,
  );

export const getTestReportExport = (
  seedTestId: string,
  payload: TestReportExportPayload,
) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test/${seedTestId}/export`,
    payload,
  );

export const getSequenceSubsequenceList = async () =>
  api.get<ResponseSuccess>(
    'sequences/list?numberOfSteps=gt:0&fetchChildSequences=1',
  );

export const getSequenceStepList = async (sequenceId) =>
  api.get<ResponseSuccess>(`/sequences/${Number(sequenceId)}/steps?type=1`);

export const getShareableReportData = (
  seedTemplateId: string,
  payload: GetShareableReportDataPayload,
) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test-template/${seedTemplateId}/shareable/analytics`,
    payload,
  );

export const getShareableReportSenderEmailAccounts = (
  seedTemplateId: string,
  payload: GetShareableReportSenderEmailAccountsPayload,
) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test-template/${seedTemplateId}/shareable/list`,
    payload,
  );

export const deleteTestReport = async (testTemplateId: string) =>
  api.delete<ResponseSuccess>(
    `/inbox-analyzer/seed-test-template/${testTemplateId}`,
  );

export const getEmailReputation = (
  seedTestId: string,
  payload: GetEmailAuthenticationPayload,
) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test/${seedTestId}/email-reputation`,
    payload,
  );

export const getSpammyEmailAccounts = (seedTestId: string) =>
  api.get<ResponseSuccess>(
    `/inbox-analyzer/seed-test/${seedTestId}/spammy-email-accounts`,
  );

export const getSpamAssassinReport = (
  seedTestId: string,
  payload: { seedTestSenderEmailAccountId: number },
  filter: SpamAssassinScoreReportFilter = SpamAssassinScoreReportFilter.Positive,
) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test/${seedTestId}/spam-report?filter=${filter}`,
    payload,
  );

export const getTestReportEsps = (
  seedTestId: string,
  payload: TestReportSummaryPayload,
) =>
  api.post<ResponseSuccess>(
    `/inbox-analyzer/seed-test/${seedTestId}/esp-to-esp`,
    payload,
  );
